import type { TenantSummary } from "@/services/authService";

/**
 * Picks the ONE thing an accountant should do about a tenant, plus how many
 * other things are waiting.
 *
 * `severity` is a three-level state (urgent | attention | ok) and SEVERITY_RANK
 * only ORDERS by it, so the cartera told an accountant which clients were
 * unwell and left them to open each one to find out why. Everything needed to
 * say it was already on the row — this reads `flags` and the counts beside
 * them, and needs no backend change.
 *
 * Selection lives here rather than in the card for the same reason
 * nextObligation.ts exists: the ordering is a judgement call that has to be
 * testable, and it was previously spread across ~11 sequential `pills.push`
 * branches where no test could reach it.
 *
 * This function deliberately decides only WHICH reason and HOW MANY others.
 * Labels stay in the component, so the copy and its i18n keys are not dragged
 * into a pure module.
 */

export type TenantActionKey =
  | "onboarding"
  | "sin-dgi"
  | "rejected"
  | "pago-pendiente"
  | "boletos"
  | "ack"
  | "vencimiento"
  | "iva"
  | "beneficio-fiscal";

export interface TenantAction {
  key: TenantActionKey;
  urgency: "urgent" | "attention";
  /** Present only for reasons that are inherently a quantity. */
  count?: number;
  /** How many OTHER actionable reasons this tenant also has. */
  extra: number;
}

/**
 * Most blocking first.
 *
 * The ordering is a product judgement, not a derivation: it ranks "this client
 * cannot invoice at all" above "this client owes money". An accountant focused
 * on collections would reasonably move `pago-pendiente` and `boletos` to the
 * top, and this array is the single place to do it.
 *
 * `rejected` is not one of the AccountantTenantFlag values — it is derived from
 * rejectedInvoices, which the backend also folds into `isUrgent`. It sits with
 * the DGI failures because a rejected CFE means invoicing is already broken,
 * not merely at risk.
 */
const PRIORITY: readonly TenantActionKey[] = [
  "onboarding",
  "sin-dgi",
  "rejected",
  "pago-pendiente",
  "boletos",
  "ack",
  "vencimiento",
  "iva",
  "beneficio-fiscal",
];

const URGENT: ReadonlySet<TenantActionKey> = new Set<TenantActionKey>([
  "onboarding",
  "sin-dgi",
  "rejected",
  "pago-pendiente",
  "boletos",
]);

/** Every reason that currently applies, already in priority order. */
function applicableKeys(tenant: TenantSummary): TenantActionKey[] {
  const flags = new Set(tenant.flags);
  return PRIORITY.filter((key) => {
    if (key === "rejected") return tenant.rejectedInvoices > 0;
    // "DGI sin configurar" is implied by an unfinished onboarding, so showing
    // both would spend the +N on a restatement of the first line.
    if (key === "sin-dgi") return flags.has("sin-dgi") && !flags.has("onboarding");
    return flags.has(key);
  });
}

export function describeTenantAction(tenant: TenantSummary): TenantAction | null {
  // An offboarded tenant is a STATE, not a task. The card badges it directly
  // and suppresses action pills; surfacing "3 boletos sin pagar" for a client
  // who left would be asking for work that must not be done.
  if (tenant.bajaAt != null) return null;

  const keys = applicableKeys(tenant);
  const key = keys[0];
  if (!key) return null;

  return {
    key,
    urgency: URGENT.has(key) ? "urgent" : "attention",
    count: countFor(tenant, key),
    extra: keys.length - 1,
  };
}

function countFor(
  tenant: TenantSummary,
  key: TenantActionKey,
): number | undefined {
  if (key === "boletos") return tenant.pendingBoletosThisMonth;
  if (key === "ack") return tenant.pendingAckCount;
  if (key === "rejected") return tenant.rejectedInvoices;
  return undefined;
}
